import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, MenuItem, TextField, Grid, Typography } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { getApiErrorMessage, prepareAndGetMediaURL } from '../../utilities/helper';
import { APIResponse, Item, SubItem } from '../../utilities/types';
import CustomAlert from '../elements/custom-alert.element';
import { addSubItemApi, updateSubItemApi } from '../../apis/admin';
import MediaUploadElement from '../elements/media-upload.element';
import { getCatalogItems } from '../../apis';

const validationKeys: any = {
  name: Yup.string().required('Name is required'),
};

const defaultInitialValues: SubItem = {
  id: 0,
  item_id: 0,
  name: '',
  description: '',
  is_active: false,
  image: null,
};

interface FormProps {
  item: SubItem | null;
  onSuccess?: () => void;
}

const SubItemForm: React.FC<FormProps> = ({ item, onSuccess }) => {
  const [initialValues, setInitialValues] = useState<SubItem>({ ...defaultInitialValues });
  const [saveApiError, setSaveApiError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);

  const onSave = useCallback(
    async (values: SubItem) => {
      try {
        const form = new FormData();
        if (values.id) {
          form.append('id', values.id.toString());
        }
        form.append('item_id', values.item_id.toString());
        form.append('name', values.name);
        form.append('description', values.description ?? '');
        form.append('is_active', values.is_active);
        if (selectedFile) {
          form.append('image', selectedFile, selectedFile.name);
        }
        setSaveApiError(null);
        const response = (await (!values.id
          ? addSubItemApi(form)
          : updateSubItemApi(form))) as APIResponse<SubItem>;
        if (response.status) {
          onSuccess?.();
        }
      } catch (err) {
        setSaveApiError(getApiErrorMessage(err));
      }
    },
    [onSuccess, selectedFile]
  );

  useEffect(() => {
    if (item) {
      setInitialValues(item);
      setPreviewUrl(prepareAndGetMediaURL(item.image));
    }
  }, [item]);

  useEffect(() => {
    const payload = {
      limit: 100,
    };
    getCatalogItems(payload)
      .then((response: any) => {
        if (response.status) {
          setItems(response.data.items);
        }
      })
      .catch((err) => {
        console.log('===========Error in feteching category list', err);
      });
  }, []);

  useEffect(() => {
    // cleanup blob url on unmount or file change
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const onFileChange = (file?: File | null) => {
    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }
    setSelectedFile(file);
    // create preview blob url
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const validationSchema = Yup.object(validationKeys);

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={(values) => {
          console.log('Form Submitted ✅', values);
          onSave(values);
        }}
      >
        {({ values, errors, touched, handleChange, isValid, dirty }) => (
          <Form>
            {saveApiError && (
              <Box sx={{ my: 1 }}>
                {saveApiError && <CustomAlert type="error">{saveApiError}</CustomAlert>}
              </Box>
            )}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  fullWidth
                  name="name"
                  label="Name *"
                  margin="normal"
                  value={values.name}
                  onChange={handleChange}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
                <Field
                  as={TextField}
                  select
                  fullWidth
                  name="is_active"
                  label="Is Active"
                  margin="normal"
                  value={values.is_active ? '1' : '0'}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="1">Yes</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2">Image</Typography>
                <MediaUploadElement
                  previewURL={previewUrl}
                  onSelect={onFileChange}
                  onRemove={handleRemoveImage}
                />
              </Grid>
            </Grid>
            <Field
              as={TextField}
              select
              fullWidth
              name="item_id"
              label="Item"
              margin="normal"
              value={values.item_category_id}
            >
              <MenuItem value="">Select</MenuItem>
              {items.length > 0 &&
                items.map((c, i) => (
                  <MenuItem key={`category-index-${i}`} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
            </Field>
            <Field
              as={TextField}
              fullWidth
              rows={4}
              multiline
              name="description"
              label="Description (Optional)"
              margin="normal"
              value={values.description}
              onChange={handleChange}
              error={touched.description && Boolean(errors.description)}
              helperText={touched.description && errors.description}
            />

            {/* Submit */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 3 }}
              disabled={!isValid || !dirty}
            >
              Submit
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default SubItemForm;
